import { apiClient } from '@/api/client'

import type { BookChatCitation } from './book-ai'

/** 章ごとの、目的との関連の強さ（その本の中での相対） */
export type PurposeSectionScore = {
  index: number
  title: string
  start: number
  score: number
  level: 'high' | 'mid' | 'low'
  /** 目的に効く箇所のうち、この章にある数 */
  passage_count: number
}

/** 目的に効く本文の断片（本文に印を付けるため全文も持つ） */
export type PurposePassage = BookChatCitation & { text: string; score: number }

export type PurposeMap = {
  /** 本全体が目的に合っているか */
  fit: 'good' | 'partial' | 'weak'
  sections: PurposeSectionScore[]
  /** 目的に近い順 */
  passages: PurposePassage[]
}

export type PurposePlanAction = 'read' | 'skim' | 'skip'

export type PurposePlan = {
  approach: string
  sections: { index: number; action: PurposePlanAction; reason: string; prerequisite: boolean }[]
}

export type PracticeItem = { title: string; detail: string; citations: BookChatCitation[] }

export type PurposePractice = {
  learnings: PracticeItem[]
  actions: PracticeItem[]
  until_key: number | null
  created_at: string
}

/** 目的を磨くときに AI が聞いた質問と、読者の答え */
export type PurposeContextItem = { question: string; answer: string }

/** この本を読む目的と、目的に合わせて作ったもの */
export type BookAiPurpose = {
  purpose: string
  /** AI と磨く前に最初に書いた目的（磨いていなければ null） */
  original_purpose: string | null
  /** 磨くときに答えた読者の状況 */
  context: PurposeContextItem[]
  updated_at: string
  /** 解析前・目的と本文を比べられなかったときは null */
  map: PurposeMap | null
  plan: PurposePlan | null
  /** 読み方の提案を作ったあとに本の解析が変わった */
  plan_outdated: boolean
  practice: PurposePractice | null
  /** 章の番号 → 目的に沿った「読む前の問い」 */
  section_questions: Record<string, string[]>
}

// AI の生成を待つため、通常より長く待つ
const AI_TIMEOUT_MS = 2 * 60_000

const purposeUrl = (episodeId: number) => `/book-ai/episodes/${episodeId}/purpose`

/** 読む目的（未設定なら null） */
export const getPurpose = async (episodeId: number): Promise<BookAiPurpose | null> => {
  const res = await apiClient.get<BookAiPurpose | null>(purposeUrl(episodeId))
  return res.data ?? null
}

/** AI と磨いて決めたときに一緒に残すもの（省くと今のまま） */
export type PurposeOrigin = { original_purpose: string; context: PurposeContextItem[] }

/** 読む目的を決める・書き換える（書き換えると、目的から作ったものは消える） */
export const putPurpose = async (
  episodeId: number,
  purpose: string,
  origin?: PurposeOrigin,
): Promise<BookAiPurpose> => {
  const res = await apiClient.put<BookAiPurpose>(
    purposeUrl(episodeId),
    { purpose, ...origin },
    { timeout: AI_TIMEOUT_MS },
  )
  return res.data
}

/** 目的とこの本の合い方（目的と本文の埋め込みの近さから測る） */
export type PurposeFitPreview = { fit: PurposeMap['fit']; sections: PurposeSectionScore[] }

export type PurposeRefinement = {
  /** この本で答えられること / 答えにくいこと */
  can_answer: string[]
  hard_to_answer: string[]
  /** 読者の状況を聞く質問（選択肢付き） */
  questions: { question: string; options: string[] }[]
  /** 磨いた目的の案と、それぞれの合い方 */
  proposals: { purpose: string; reason: string; preview: PurposeFitPreview | null }[]
  /** 下書きのままの合い方 */
  draft_preview: PurposeFitPreview | null
}

/** 書いた目的を、この本をうまく使える形に AI と磨く（保存はしない） */
export const refinePurpose = async (
  episodeId: number,
  purpose: string,
  answers: PurposeContextItem[],
): Promise<PurposeRefinement> => {
  const res = await apiClient.post<PurposeRefinement>(
    `${purposeUrl(episodeId)}/refine`,
    { purpose, answers },
    { timeout: AI_TIMEOUT_MS },
  )
  return res.data
}

export const deletePurpose = async (episodeId: number): Promise<void> => {
  await apiClient.delete(purposeUrl(episodeId))
}

/** 章ごとの読み方の提案を作る */
export const createPurposePlan = async (episodeId: number): Promise<PurposePlan> => {
  const res = await apiClient.post<PurposePlan>(`${purposeUrl(episodeId)}/plan`, undefined, {
    timeout: AI_TIMEOUT_MS,
  })
  return res.data
}

/**
 * 実践メモ（分かったこと・試すこと）を作る
 * @param untilKey ネタバレ防止: この位置キーまでの本文だけで作る（null なら本全体）
 */
export const createPurposePractice = async (episodeId: number, untilKey: number | null): Promise<PurposePractice> => {
  const res = await apiClient.post<PurposePractice>(
    `${purposeUrl(episodeId)}/practice`,
    { until_key: untilKey },
    { timeout: AI_TIMEOUT_MS },
  )
  return res.data
}

/** 実践メモの 1 項目からカードを作る */
export const createPracticeCard = async (
  episodeId: number,
  body: { title: string; body: string; citation_chunk_ids: number[]; link_memo_ids: number[] },
): Promise<{ memo_id: number; title: string }> => {
  const res = await apiClient.post<{ memo_id: number; title: string }>(`${purposeUrl(episodeId)}/practice/card`, body)
  return res.data
}

/** 章の、目的に沿った「読む前の問い」（一度作った章は使い回される） */
export const getSectionPurposeQuestions = async (episodeId: number, sectionIndex: number): Promise<string[]> => {
  const res = await apiClient.post<{ questions: string[] }>(
    `${purposeUrl(episodeId)}/questions`,
    { section_index: sectionIndex },
    { timeout: AI_TIMEOUT_MS },
  )
  return res.data.questions
}

// ============================================================
// 本棚全体への質問
// ============================================================

/** 本棚の本の本文（どの本のどこか） */
export type LibraryCitation = BookChatCitation & {
  episode_id: number
  book_title: string
  media: 'epub' | 'pdf'
  score: number
}

export type LibraryChatResponse = {
  answer: string
  /** 回答の根拠にした本文 */
  citations: LibraryCitation[]
  /** 質問で見つかった本文すべて（近い順） */
  passages: LibraryCitation[]
}

export type LibraryChatTurn = { role: 'user' | 'assistant'; content: string }

/** AI 解析済みのすべての本から探して答える（履歴は保存されないので、直前の会話を送る） */
export const postLibraryChat = async (question: string, history: LibraryChatTurn[]): Promise<LibraryChatResponse> => {
  const res = await apiClient.post<LibraryChatResponse>(
    '/book-ai/library/chat',
    { question, history },
    { timeout: AI_TIMEOUT_MS },
  )
  return res.data
}
